import {
  Button,
  classes,
  Icon,
  ProgressSpinner,
  usePrevious,
} from '@dittolive/anvil'
import { CheckIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react'
import React, { useCallback, useMemo, useState } from 'react'
import { useTimeoutWhen } from 'rooks'

import type {
  LiveQueryDataBridgeStatus,
  LiveQueryDestinationKind,
} from '../../../types/dataBridge'

// The maximum time to wait for the data bridge to be provisioned before showing
// an error state (5 minutes).
const PROVISIONING_MAX_WAIT_MS = 5 * 60 * 1000

type Props = {
  /**
   * The current status of the data bridge
   */
  status: LiveQueryDataBridgeStatus | null
  /**
   * The destination kind (kafka or webhook)
   */
  destinationKind?: LiveQueryDestinationKind
  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean
  /**
   * Callback when provisioning times out
   */
  onTimeout?: () => void
  /**
   * Callback when the banner is closed
   */
  onClose?: () => void
}

export default function DataBridgeStatusIndicator({
  status,
  destinationKind,
  isLoading = false,
  onTimeout,
  onClose,
}: Props) {
  const prevStatus = usePrevious(status)
  const [closed, setClosed] = useState(false)

  const isProvisioning = useMemo(() => status === 'provisioning', [status])

  const justCreated = useMemo(
    () => prevStatus === 'provisioning' && status === 'ready',
    [prevStatus, status],
  )
  const invalidState = useMemo(
    () => !isLoading && status === 'error',
    [isLoading, status],
  )

  const renderBannerIcon = () => {
    if (isProvisioning) {
      return <ProgressSpinner className="size-4" />
    } else if (justCreated) {
      return <Icon className="size-4" svg={<CheckIcon />} />
    } else if (invalidState) {
      return <Icon className="size-4" svg={<WarningCircleIcon />} />
    } else {
      return null
    }
  }

  const renderBannerText = () => {
    if (isProvisioning) {
      return "We're setting up your data bridge"
    } else if (justCreated) {
      return 'Your data bridge is ready to use'
    } else if (invalidState) {
      const wasPreviouslyProvisioning = prevStatus === 'provisioning'
      return wasPreviouslyProvisioning
        ? 'Error provisioning data bridge'
        : 'Unprocessable data bridge'
    } else {
      return null
    }
  }

  const renderStatusText = () => {
    if (isProvisioning) {
      return 'This might take a few minutes, but you can safely close this window and come back.'
    } else if (justCreated && destinationKind) {
      return `You can now start consuming your data bridge using the ${
        destinationKind === 'webhook' ? 'webhook' : 'Kafka'
      } details below.`
    } else if (invalidState) {
      const wasPreviouslyProvisioning = prevStatus === 'provisioning'
      return wasPreviouslyProvisioning
        ? 'There was an error provisioning your data bridge'
        : 'Your data bridge is in an invalid state'
    }
  }

  const hasStatus = isProvisioning || justCreated || invalidState
  const isCloseable = !isProvisioning && !invalidState

  /**
   * A callback to notify parent if the data bridge is taking too long to provision
   */
  const checkProvisioningCompleted = useCallback(() => {
    if (isProvisioning && onTimeout) {
      onTimeout()
    }
  }, [isProvisioning, onTimeout])

  useTimeoutWhen(
    checkProvisioningCompleted,
    PROVISIONING_MAX_WAIT_MS,
    isProvisioning,
  )

  const handleClose = () => {
    setClosed(true)
    onClose?.()
  }

  if (!hasStatus || closed) {
    return null
  }

  return (
    <div data-testid="dataBridgeStatus" className="overflow-hidden rounded-xl">
      <div
        className={classes(
          'flex h-10 w-full items-center justify-between px-4',
          {
            'bg-fill-success': justCreated,
            'bg-fill-info': isProvisioning,
            'bg-fill-critical': invalidState,
          },
        )}
        data-testid="dataBridgeStatusBanner"
      >
        <div className="flex items-center gap-2 text-white">
          {renderBannerIcon()}
          <p className="text-base font-medium leading-5">
            {renderBannerText()}
          </p>
        </div>

        {isCloseable && (
          <Button
            data-testid="closeButton"
            size="icon"
            className="flex items-center justify-center p-1 text-white"
            onClick={handleClose}
          >
            <Icon className="size-4" svg={<XIcon />} />
          </Button>
        )}
      </div>

      <p
        className="bg-background-surface px-4 py-3"
        data-testid="dataBridgeStatusText"
      >
        {renderStatusText()}
      </p>
    </div>
  )
}
